import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, watch } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { ThemeClient } from "@/lib/clients";
import { showOkCancelDialog } from "@/lib/dialog";
import { Dialog } from "@/lib/utils";
import { Novel } from "@/lib/models";
import type { NolaPfContestExtraFieldItem } from "@/lib/models/nolaPfContest";
import { extraFieldMaxLength } from "@/lib/utils/contestEntryExtraFields";
import { CONTEST_ENTRY_SYNOPSIS_MAX_LENGTH } from "@/lib/utils/contestEntryWorkForm";
import { readManuscriptFiles } from "@/lib/utils/manuscriptFileImport";
import { getNovelCoverImageSrc } from "@/lib/utils/contestEntryNovelSelection";
import ContestEntryNovelSelectDialog, {
  ContestEntryNovelSelectDialogResult,
} from "@/components/ui/dialogs/ContestEntryNovelSelectDialog.vue";
import ImportDialog, { ImportDialogResult } from "@/components/ui/dialogs/ImportDialog.vue";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import ImageView from "@/components/atoms/ImageView.vue";
import MaskedLoading from "@/components/atoms/MaskedLoading.vue";
import ContestEntryCopySynopsisSelect, {
  ContestEntryCopySynopsisSource,
} from "@/components/organisms/event/ContestEntryCopySynopsisSelect.vue";
import type {
  ContestEntryWorkCategoryOption,
  ContestEntryWorkFormValue,
} from "@/components/organisms/event/contestEntryWorkFormTypes";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconClose from "~icons/figma/Close";
import IconNoteBook from "~icons/figma/NoteBook";
import IconUpload from "~icons/figma/Upload";


export default /*#__PURE__*/_defineComponent({
  __name: 'ContestEntryWorkSelectForm',
  props: {
    value: { type: null, required: true },
    categoryOptions: { type: Array, required: true },
    canProceed: { type: Boolean, required: true },
    novels: { type: Array, required: true },
    extraFieldItems: { type: Array, required: true }
  },
  emits: ["input", "back", "next"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", value: ContestEntryWorkFormValue): void;
  (e: "back"): void;
  (e: "next"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: ContestEntryWorkFormValue;
  categoryOptions: ContestEntryWorkCategoryOption[];
  canProceed: boolean;
  novels: Novel[];
  extraFieldItems: NolaPfContestExtraFieldItem[];
};

const novelSelectDialog = new Dialog(ContestEntryNovelSelectDialog);
const importDialog = new Dialog(ImportDialog);
const themeClient = new ThemeClient();





const { t } = useI18n();
const instance = getCurrentInstance();
const isImporting = ref(false);
const appliedSynopsis = ref<string | null>(null);

watch(
  () => props.value.novelId,
  () => {
    appliedSynopsis.value = null;
  }
);

const hasSelectedNolaWork = computed(() => props.value.workSource === "nola" && !!props.value.novelId);

const isSourceSelectDisabled = computed(() => props.categoryOptions.length > 0 && !props.value.categoryId);

function updateField<K extends keyof ContestEntryWorkFormValue>(field: K, fieldValue: ContestEntryWorkFormValue[K]) {
  emit("input", { ...props.value, [field]: fieldValue });
}

function onCategoryChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  emit("input", {
    ...props.value,
    categoryId: target.value,
    novelId: "",
    novelTitle: "",
    novelImage: "",
    novelDescription: "",
    workTitle: "",
    synopsis: "",
    workSource: "",
  });
}

function showNotice(title: string, content: string, isError = false) {
  const dialog = new Dialog(SimpleDialog);
  const data: SimpleDialogProps = {
    title,
    content,
    isError,
    width: 350,
  };
  dialog.show(data);
}

function selectNolaWork(work: { novelId: string; title: string; image: string; description: string }) {
  appliedSynopsis.value = null;
  emit("input", {
    ...props.value,
    workSource: "nola",
    novelId: work.novelId,
    novelTitle: work.title,
    novelImage: work.image,
    novelDescription: work.description,
    workTitle: work.title,
    synopsis: "",
  });
}

function selectCreatedNovel(novelId: string, fallbackTitle: string) {
  const novels = (instance!.proxy.$store.getters["novelModule/novels"] as Novel[]) ?? [];
  const novel = novels.find((item) => item.novelId === novelId);
  const title = novel?.title?.trim() || fallbackTitle.trim() || (t("common.untitled") as string);

  selectNolaWork({
    novelId,
    title,
    image: novel ? getNovelCoverImageSrc(novel) : "",
    description: novel?.description ?? "",
  });
}

async function onClickImportFromLocal() {
  if (props.categoryOptions.length > 0 && !props.value.categoryId) return;

  const result = (await importDialog.show({ hideScriptMode: true })) as ImportDialogResult | null;
  if (!result) return;

  isImporting.value = true;
  try {
    const payloads = await readManuscriptFiles(result.files, false);

    const created = (await instance!.proxy.$store.dispatch("novelModule/createNovel", {
      title: result.title,
      manuscriptMode: "default",
    })) as Novel;

    await instance!.proxy.$store.dispatch("manuscriptModule/createMultipleManuscript", {
      params: payloads.map((item) => ({
        novelId: created.novelId,
        title: item.title || "",
        content: item.content || "",
      })),
    });
    await instance!.proxy.$store.dispatch("novelModule/initialize");

    selectCreatedNovel(created.novelId, result.title);
  } catch (error) {
    console.error(error);
    showNotice(
      t("homePage.novelList.import.errorTitle") as string,
      t("homePage.novelList.import.errorMessage") as string,
      true
    );
  } finally {
    isImporting.value = false;
  }
}

async function onClickSelectFromNola() {
  if (props.categoryOptions.length > 0 && !props.value.categoryId) return;

  const result = (await novelSelectDialog.show({
    title: t("contestEntryForm.step2.novelSelect.title"),
    novels: props.novels,
    initialNovelId: props.value.novelId,
  })) as ContestEntryNovelSelectDialogResult | null;

  if (!result) return;

  selectNolaWork({
    novelId: result.novelId,
    title: result.title,
    image: result.image,
    description: result.description,
  });
}

function clearSelectedWork() {
  emit("input", {
    ...props.value,
    workSource: "",
    novelId: "",
    novelTitle: "",
    novelImage: "",
    novelDescription: "",
    workTitle: "",
    synopsis: "",
  });
}

function onWorkTitleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  updateField("workTitle", target.value);
}

function onSynopsisInput(event: Event) {
  const target = event.target as HTMLTextAreaElement;
  updateField("synopsis", target.value);
}

function applySynopsis(text: string) {
  const synopsis = text.slice(0, CONTEST_ENTRY_SYNOPSIS_MAX_LENGTH);
  appliedSynopsis.value = synopsis;
  updateField("synopsis", synopsis);
}

function showUnsetSynopsisNotice(messageKey: "novelEmpty" | "themeEmpty") {
  showNotice(t("common.confirmTitle") as string, t(`contestEntryForm.step2.copySynopsis.${messageKey}`) as string);
}

async function resolveCopySynopsis(source: ContestEntryCopySynopsisSource): Promise<string | null> {
  if (source === "fromNovel") {
    const novelSynopsis = props.value.novelDescription ?? "";
    if (!novelSynopsis.trim()) {
      showUnsetSynopsisNotice("novelEmpty");
      return null;
    }
    return novelSynopsis;
  }

  try {
    const theme = await themeClient.fetchTheme(props.value.novelId);
    const themeSynopsis = theme?.detail?.find((detail) => detail.name === "synopsis")?.value ?? "";
    if (!themeSynopsis.trim()) {
      showUnsetSynopsisNotice("themeEmpty");
      return null;
    }
    return themeSynopsis;
  } catch (error) {
    console.error(error);
    showNotice(
      t("contestEntryForm.step2.submissionWork") as string,
      t("contestEntryForm.step2.copySynopsis.themeLoadError") as string,
      true
    );
    return null;
  }
}

async function onCopySynopsisSelect(source: ContestEntryCopySynopsisSource) {
  const synopsis = await resolveCopySynopsis(source);
  if (synopsis === null) return;

  const hasManualSynopsis = props.value.synopsis.trim().length > 0 && props.value.synopsis !== appliedSynopsis.value;
  if (hasManualSynopsis) {
    const confirmed = await showOkCancelDialog({
      title: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.title") as string,
      content: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.content") as string,
      okButton: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.ok") as string,
      cancelButton: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.cancel") as string,
      okVariant: "primary",
    });
    if (!confirmed) return;
  }

  applySynopsis(synopsis);
}

function extraAnswer(key: string): string {
  return props.value.extraAnswers?.[key] ?? "";
}

function onExtraAnswerInput(key: string, event: Event) {
  const target = event.target as HTMLInputElement;
  emit("input", {
    ...props.value,
    extraAnswers: {
      ...props.value.extraAnswers,
      [key]: target.value,
    },
  });
}

return { __sfc: true,novelSelectDialog, importDialog, themeClient, props, emit, t, instance, isImporting, appliedSynopsis, hasSelectedNolaWork, isSourceSelectDisabled, updateField, onCategoryChange, showNotice, selectNolaWork, selectCreatedNovel, onClickImportFromLocal, onClickSelectFromNola, clearSelectedWork, onWorkTitleInput, onSynopsisInput, applySynopsis, showUnsetSynopsisNotice, resolveCopySynopsis, onCopySynopsisSelect, extraAnswer, onExtraAnswerInput, extraFieldMaxLength, ImageView, MaskedLoading, ContestEntryCopySynopsisSelect, IconChevronDown, IconClose, IconNoteBook, IconUpload }
}

})